import type {AggregationSurveySubmissionListItem}
    from "crqlar-react-common/dist/models/aggregation/surveyAggregation.model";
import type {SurveyQuestion} from "crqlar-react-common/dist/models/survey/surveyQuestion.model";
import {SurveyAggregationService} from "crqlar-react-common/dist/services/aggregation/surveyAggregation.service";
import {useEffect, useMemo, useState} from "react";
import {useQuery} from "react-query";
import type {SurveyAnswerContext} from "../shared/surveyAnswer.util";
import {isWithheldSensitiveAnswer} from "../shared/SurveySensitiveAnswerValue";

export interface SurveySubmissionSensitiveReveal {
    /** The listed row has withheld sensitive answers, so the banner offers to show them. */
    offered: boolean;
    revealed: boolean;
    fetching: boolean;
    toggle: () => void;
    /** The revealed copy while it is shown, otherwise the row as listed. */
    shownSubmission: AggregationSurveySubmissionListItem;
}

interface Args {
    submission: AggregationSurveySubmissionListItem;
    questions: SurveyQuestion[];
    ctx: SurveyAnswerContext;
    open: boolean;
}

/**
 * Showing a reply's sensitive answers is a second request for that one submission, under its own key: hiding them
 * again shows the withheld row at once, and no revealed text sits under a key the lists read.
 */
export function useSurveySubmissionSensitiveReveal({
    submission,
    questions,
    ctx,
    open
}: Args): SurveySubmissionSensitiveReveal {
    const [revealSensitive, setRevealSensitive] = useState(false);

    const revealQuery = useQuery(
        ['surveySubmissionSensitive', submission.id],
        () => SurveyAggregationService.listSubmissions({
            id: [submission.id],
            includeSensitive: true,
            limit: {offset: 0, limit: 1}
        }),
        {enabled: open && revealSensitive && !!submission.id, refetchOnWindowFocus: false}
    );
    const revealedSubmission = revealQuery.data?.content?.[0];
    const revealed = revealSensitive && !!revealedSubmission;
    const shownSubmission = revealed ? revealedSubmission : submission;

    useEffect(() => setRevealSensitive(false), [submission.id]);

    const offered = useMemo(
        () => questions.some(question => isWithheldSensitiveAnswer(
            submission.answers?.find(answer => answer.questionId === question.id),
            question,
            ctx
        )),
        [questions, submission, ctx]
    );

    return {
        offered,
        revealed,
        fetching: revealQuery.isFetching,
        toggle: () => setRevealSensitive(!revealed),
        shownSubmission,
    };
}
